import React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { createPortal } from 'react-dom';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import cn from 'classnames';

import Flex from 'src/components/common/Flex';
import { analytics } from 'src/utils/analytics';
import { useAppSelector } from 'src/hooks/store';
import { selectConfig } from 'src/store/config/selectors';
import Filters from './Filters';
import { useSearch } from './Context';
import { useResponsive } from 'src/hooks/responsive';

const Container = styled(
  (props: React.PropsWithChildren & { className?: string }) => (
    <Flex {...props} justifyContent="space-between" alignItems="center">
      {props.children}
    </Flex>
  ),
  { label: 'SearchContainer' },
)(({ theme }) => ({
  position: 'relative',
  width: '574px',
  height: '51px',
  padding: '6px',
  borderRadius: '10px',
  border: `1px solid ${theme.palette.other.formBorderLight}`,
  backgroundColor: '#FFFFFF',
  color: '#04476D',

  [theme.breakpoints.down('md')]: {
    width: '356px',
  },

  [theme.breakpoints.down('xs')]: {
    width: '294px',
  },
}));

const SearchInput = styled(InputBase, { label: 'SearchInput' })({
  color: 'inherit',
  flexGrow: 1,

  '& input': {
    fontSize: '18px',
    lineHeight: '19.71px',
    textAlign: 'right',
    padding: '0',
    transform: 'translateY(1px)',
  },
});

const SearchButton = styled('button', { label: 'SearchButton' })({
  cursor: 'pointer',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '32px',
  height: '32px',
  marginLeft: '1px',
  padding: '0',
  border: 'none',
  borderRadius: '50%',
  color: '#7D8D96',
  backgroundColor: 'transparent',

  '& img': {
    position: 'relative',
    display: 'block',
    width: '19px',
    height: '19px',
  },

  '&:hover': {
    '& path': {
      fill: '#053550',
    },
  },

  '&:disabled': {
    cursor: 'default',
    pointerEvents: 'none',
  },
});

const SearchIcon = styled(
  ({ color = '#7D8D96', ...props }: { color?: string }) => (
    <svg {...props} width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.65 0C2.98856 0 0 2.98856 0 6.65C0 10.3114 2.98856 13.3 6.65 13.3C8.3106 13.3 9.82776 12.6806 10.9955 11.6672L11.4 12.0717V13.3L17.1 19L19 17.1L13.3 11.4H12.0717L11.6672 10.9955C12.6806 9.82776 13.3 8.3106 13.3 6.65C13.3 2.98856 10.3114 0 6.65 0ZM6.65 1.9C9.2846 1.9 11.4 4.01539 11.4 6.65C11.4 9.2846 9.2846 11.4 6.65 11.4C4.01539 11.4 1.9 9.2846 1.9 6.65C1.9 4.01539 4.01539 1.9 6.65 1.9Z"
        fill={color}
      />
    </svg>
  ),
  { label: 'SearchIcon' },
)(({ theme }) => ({
  position: 'relative',
  display: 'block',

  '& path': {
    transition: 'fill 0.3s',
  },

  [theme.breakpoints.down('md')]: {
    marginTop: '5px',
    marginLeft: '4px',
  },
}));

const FilterBtn = styled(
  (props: React.PropsWithChildren & { open?: boolean; active?: boolean }) => (
    <button {...props} type="button">
      {props.children}
    </button>
  ),
  { label: 'FilterBtn', shouldForwardProp: (prop) => prop !== 'active' },
)<React.HTMLProps<HTMLButtonElement>>(({ theme, open, active }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  border: 'none',
  gap: '8px',
  position: 'relative',
  backgroundColor: open ? theme.palette.other.formBackground : 'transparent',
  padding: '12px 11px',
  borderRadius: '5px',
  transition: 'background-color 0.3s',

  ...(!open
    ? {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          right: '0',
          transform: 'translateY(-50%)',
          display: 'block',
          width: '1px',
          height: '24px',
          backgroundColor: alpha('#000', 0.2),
        },
      }
    : {
        '&:hover': {
          backgroundColor: theme.palette.other.formBackgroundLight,
        },
      }),

  '& p': {
    fontSize: '16px',
    lineHeight: '14px',
    margin: '0',
    whiteSpace: 'nowrap',
    color: '#053550',
    transform: 'translateY(1px)',
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    display: active ? 'block' : 'none',
    top: '2px',
    left: '2px',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#FF0003',
  },
}));

export const FiltersIcon = styled(
  ({ color = '#0F3C55' }: { color?: string }) => (
    <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0 2V3.75H8.75V2H0ZM3.5 5.5V7.25H0V9H3.5V10.75H5.25V5.5H3.5ZM15.75 9V7.25H7V9H15.75ZM10.5 5.5H12.25V3.75H15.75V2H12.25V0.25H10.5V5.5Z"
        fill={color}
      />
    </svg>
  ),
  { label: 'SearchIcon' },
)({
  position: 'relative',
  display: 'block',
});

interface Props {
  className?: string;
}

function Search(props: Props) {
  const { className = '' } = props;
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
  const t = useAppSelector(selectConfig).texts.website;
  const search = useSearch();
  const [isMobile] = useResponsive('MOBILE');
  const [isSmallMobile] = useResponsive('ULTRA_SMALL_MOBILE');
  const isInProgress = search.isFetching;

  const placeholderText = isSmallMobile
    ? t.main.header.search.placeholderSmallMobile
    : t.main.header.search.placeholder;

  const onSearchInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      search.formik.setFieldValue('query', e.target.value);
    },
    [search.formik],
  );

  const onSubmitBtnClick = React.useCallback(() => {
    search.formik.setFieldValue('page', 1);
    // don't submit formik here, because it will be submitted on form submit, this btn hase type="submit"
  }, [search.formik]);

  const onFiltersToggleClick = React.useCallback(() => {
    setIsFiltersOpen((prev) => !prev);
  }, []);

  return (
    <>
      <form onSubmit={search.formik.handleSubmit}>
        <Container className={cn(className)}>
          <Flex width="100%" alignItems="center">
            <SearchButton disabled={isInProgress} type="submit" onClick={onSubmitBtnClick}>
              {!isInProgress ? <SearchIcon /> : <CircularProgress size={20} color="inherit" />}
            </SearchButton>
            <SearchInput
              placeholder={placeholderText}
              value={search.formik.values.query}
              onChange={onSearchInputChange}
            />
          </Flex>
          <FilterBtn
            open={isFiltersOpen}
            active={search.isFilterActive && !isFiltersOpen}
            onClick={onFiltersToggleClick}
          >
            <FiltersIcon />
            {!isMobile && <Typography component="p">{t.main.header.search.filter.btnLabel}</Typography>}
          </FilterBtn>
        </Container>
      </form>
      {isFiltersOpen &&
        (isMobile ? createPortal(<Filters onClose={() => setIsFiltersOpen(false)} />, document.body) : <Filters />)}
    </>
  );
}

export default Search;
